import React from 'react';
import { GradeLevel, IQualification, IStudy, ResultStatus } from 'types';
import { getUniqueKeysFromObjects, identifyResultStatusFromGrouping } from '../helpers/findUniqueIds';
import { CustomCollapse } from '@/components/CustomCollapse/CustomCollapse';
import Field from '@/components/Field/Field';
import { getGradeDisplay, getDateDisplay } from '../helpers/formatting';
import { QualificationModules } from './QualificationModules';

export interface IQualificationsProps {
  studyCurrent?: IStudy;
  studyInitial?: IStudy;
}

export function Qualifications(props: IQualificationsProps): React.ReactElement {
  const ids = getUniqueKeysFromObjects(
    props.studyCurrent?.qualifications ?? {},
    props.studyInitial?.qualifications ?? {},
  );

  const QualificationComponents: React.ReactElement[] = [];

  for (const [index, qualId] of Array.from(ids).entries()) {
    const qualCurrent: IQualification | undefined = props.studyCurrent?.qualifications?.[qualId];
    const qualInitial: IQualification | undefined = props.studyInitial?.qualifications?.[qualId];

    let qualGroupings1 = props.studyCurrent?.qualificationTypeSelections || [];
    let qualGroupings2 = props.studyInitial?.qualificationTypeSelections || [];

    // Guard against incorrect typing
    if (!Array.isArray(qualGroupings1)) qualGroupings1 = [];
    if (!Array.isArray(qualGroupings2)) qualGroupings2 = [];

    // If both qualifications are missing skip
    if (!qualCurrent && !qualInitial) continue;

    QualificationComponents.push(
      <Qualification
        key={`${qualId}-${index}`}
        id={qualId}
        qualCurrent={qualCurrent}
        qualInitial={qualInitial}
        qualGroupingsCurrent={qualGroupings1}
        qualGroupingsInitial={qualGroupings2}
      />,
    );
  }

  return <>{QualificationComponents}</>;
}

export interface IQualificationProps {
  id: string;
  qualCurrent?: IQualification;
  qualInitial?: IQualification;
  qualGroupingsCurrent?: IStudy['qualificationTypeSelections'];
  qualGroupingsInitial?: IStudy['qualificationTypeSelections'];
}

export function Qualification(props: IQualificationProps): React.ReactElement {
  const { id, qualCurrent, qualInitial, qualGroupingsCurrent, qualGroupingsInitial } = props;

  const studyResultsType1 = identifyResultStatusFromGrouping(qualGroupingsCurrent, qualCurrent);
  const studyResultsType2 = identifyResultStatusFromGrouping(qualGroupingsInitial, qualInitial);

  const isPending1 = studyResultsType1 === ResultStatus.NoResults;
  const isPending2 = studyResultsType2 === ResultStatus.NoResults;

  // Show qualification type label and subject (if exists)
  const qualificationLabel1 =
    qualCurrent?.qualificationTypeLabel ||
    qualCurrent?.otherQualificationName ||
    qualCurrent?.levelLabel ||
    (qualCurrent?.level && GradeLevel[qualCurrent?.level as keyof typeof GradeLevel]) ||
    qualCurrent?.level;
  const qualificationLabel2 =
    qualInitial?.qualificationTypeLabel ||
    qualInitial?.otherQualificationName ||
    qualInitial?.levelLabel ||
    (qualInitial?.level && GradeLevel[qualInitial?.level as keyof typeof GradeLevel]) ||
    qualInitial?.level;

  const heading1 = `${qualificationLabel1 || 'Unknown qualification'} in ${qualCurrent?.subject || 'unknown subject'}`;

  const heading2 = `${qualificationLabel2 || 'Unknown qualification'} in ${qualInitial?.subject || 'unknown subject'}`;

  const useDisplayLabel2 = !qualificationLabel1 && !qualCurrent?.subject;

  const displayLabel = useDisplayLabel2 ? heading2 : heading1;

  const gradeDisplay1 = getGradeDisplay(isPending1, studyResultsType1, qualCurrent);
  const gradeDisplay2 = getGradeDisplay(isPending2, studyResultsType2, qualInitial);

  const qualDate1 = getDateDisplay(isPending1, qualCurrent);
  const qualDate2 = getDateDisplay(isPending2, qualInitial);

  const awardBody1 = isPending1 ? 'Pending' : qualCurrent?.awardingOrganisationLabel;
  const awardBody2 = isPending2 ? 'Pending' : qualInitial?.awardingOrganisationLabel;

  const showAwardingOrganisationField =
    isPending1 || qualCurrent?.awardingOrganisationLabel || isPending2 || qualInitial?.awardingOrganisationLabel;

  const subject1 = qualCurrent?.subject;
  const subject2 = qualInitial?.subject;

  return (
    <CustomCollapse defaultIsOpen key={id}>
      <CustomCollapse.Trigger>{displayLabel}</CustomCollapse.Trigger>
      <CustomCollapse.Content>
        <Field leftLabel="Subject" value={subject1 || 'Not specified'} originalValue={subject2 || 'Not specified'} />
        <Field leftLabel="Grade" value={gradeDisplay1} originalValue={gradeDisplay2} />
        <Field leftLabel="Qualification date" value={qualDate1} originalValue={qualDate2} />
        {showAwardingOrganisationField && (
          <Field leftLabel="Awarding organisation" value={awardBody1} originalValue={awardBody2} />
        )}

        <QualificationModules qualificationCurrent={qualCurrent} qualificationInitial={qualInitial} />
      </CustomCollapse.Content>
    </CustomCollapse>
  );
}
